// TreatmentCategoriesGrid
// Compact treatment-category grid shown right after the hero on the home page,
// so visitors see every treatment Karine offers immediately and can tap to
// browse / book without scrolling past educational content first.
//
// Mirrors the "Explore Our Treatments" grid that lives inside ServicesSection,
// but rendered standalone for above-the-fold positioning.
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import {
  Crown, Syringe, Sparkles, Heart, Droplets, Zap, ArrowRight, Plus, Leaf,
} from "lucide-react";

// Oct 25 2026 · Karine: "make all the booking boxes photo realistic so it's
// easier for clients to recognize" — every card now carries a real photo
// (public/backdrops) under a dark tint; the colour gradient is the fallback.
export const TREATMENT_CATEGORY_CARDS = [
  { name: "Promotions",    nameFr: "Promotions",            icon: Crown,    href: "/promotions",            photo: "/backdrops/promotions.jpg",   color: "from-amber-500 to-yellow-600",  accent: "text-amber-200", border: "border-amber-400 hover:border-amber-300",   featured: true },
  { name: "Éminence Facials & Body", nameFr: "Soins Éminence — Visage & Corps", icon: Leaf, href: "/treatments/eminence-facials", photo: "/backdrops/facials.jpg", color: "from-emerald-900 via-emerald-950 to-[#1a2f1a]", accent: "text-emerald-200", border: "border-emerald-500/40 hover:border-emerald-300", tagline: "Certified organic · Ottawa only", taglineFr: "Certifié biologique · Ottawa seulement" },
  { name: "Botox",         nameFr: "Botox",                 icon: Syringe,  href: "/botox-ottawa",          photo: "/backdrops/botox.jpg",        color: "from-rose-800 to-rose-950",     accent: "text-rose-200",  border: "border-rose-500/40 hover:border-rose-300" },
  { name: "Icon Laser · Redness & Brown Spots", nameFr: "Laser Icon · Rougeurs & taches brunes", icon: Zap, href: "/icon-laser", photo: "/icon/icon-bg.jpg", color: "from-amber-700 to-stone-900", accent: "text-amber-200", border: "border-amber-400/60 hover:border-amber-300", tagline: "NEW · Cynosure Icon IPL · with Tanya · Ottawa", taglineFr: "NOUVEAU · IPL Cynosure Icon · avec Tanya · Ottawa", isNew: true },
  { name: "Dermal Fillers",nameFr: "Agents de Comblement",  icon: Sparkles, href: "/dermal-fillers",        photo: "/backdrops/fillers.jpg",      color: "from-purple-800 to-purple-950", accent: "text-purple-200",border: "border-purple-500/40 hover:border-purple-300" },
  { name: "Sculptra",      nameFr: "Sculptra",              icon: Heart,    href: "/sculptra",              photo: "/backdrops/sculptra.jpg",     color: "from-amber-800 to-[#3d2410]",   accent: "text-amber-200", border: "border-amber-500/40 hover:border-amber-300" },
  { name: "Skin Boosters", nameFr: "Skin Boosters",         icon: Droplets, href: "/skin-boosters",         photo: "/backdrops/skin-boosters.jpg", color: "from-teal-800 to-teal-950",    accent: "text-teal-200",  border: "border-teal-500/40 hover:border-teal-300" },
  { name: "Women's Skin & Laser Studio", nameFr: "Studio Peau & Laser (Femmes)", icon: Sparkles, href: "/laser-hair-removal", photo: "/backdrops/laser-hair.jpg", color: "from-[#7a4a1e] to-[#3d2410]", accent: "text-amber-100", border: "border-amber-500/40 hover:border-amber-200", tagline: "Laser Hair · Hydrafacial · Microneedling · Peels · From $15", taglineFr: "Épilation Laser · Hydrafacial · Microneedling · Peelings · Dès 15 $" },
  { name: "Men's Skin & Laser Studio", nameFr: "Studio Peau & Laser (Hommes)", icon: Crown, href: "/laser-hair-removal", photo: "/backdrops/mens-studio.jpg", color: "from-slate-700 to-slate-950", accent: "text-slate-200", border: "border-slate-400/40 hover:border-slate-300", tagline: "Laser Hair · Hydrafacial · Beard · Skin Therapy · From $15", taglineFr: "Épilation Laser · Hydrafacial · Barbe · Soins peau · Dès 15 $" },
  { name: "Add-On Glow Boosters", nameFr: "Boosters Éclat (Suppléments)", icon: Plus, href: "/laser-and-skin#addons", photo: "/backdrops/glow-boosters.jpg", color: "from-emerald-700 to-emerald-950", accent: "text-emerald-100", border: "border-emerald-500/40 hover:border-emerald-200", tagline: "LED · Dermaplane · CO₂ · Lymphatic · From $45", taglineFr: "LED · Dermabrasion · CO₂ · Lymphatique · Dès 45 $" },
  { name: "Microneedling", nameFr: "Micro-Aiguilles",       icon: Zap,      href: "/medical-microneedling", photo: "/backdrops/microneedling.jpg", color: "from-sky-800 to-sky-950",      accent: "text-sky-200",   border: "border-sky-500/40 hover:border-sky-300" },
  { name: "PRP Therapy",   nameFr: "Thérapie PRP",          icon: Heart,    href: "/prp-ottawa",            photo: "/backdrops/prp.jpg",          color: "from-red-800 to-red-950",       accent: "text-red-200",   border: "border-red-500/40 hover:border-red-300" },
];


export function TreatmentCategoryCard({ svc, isFr, testIdPrefix = "tcg" }) {
  const Icon = svc.icon;
  const isFeatured = !!svc.featured;
  return (
    <Link
      to={svc.href}
      onClick={() => window.scrollTo(0, 0)}
      className={`group relative block overflow-hidden rounded-xl border bg-gradient-to-br ${svc.color} ${svc.border} min-h-[150px] md:min-h-[190px] text-center transition-all duration-300 hover:shadow-lg hover:shadow-black/30 hover:-translate-y-1 ${
        isFeatured ? "shadow-lg ring-2 ring-amber-400/60 animate-pulse-soft" : ""
      }`}
      data-testid={`${testIdPrefix}-${svc.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <img src={svc.photo} alt={svc.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        data-testid={`${testIdPrefix}-photo-${svc.name.toLowerCase().replace(/\s+/g, '-')}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15" />
      {svc.isNew && (
        <span className="absolute top-2 right-2 z-10 rounded-full bg-amber-400 text-stone-900 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 shadow" data-testid={`${testIdPrefix}-new-badge-${svc.name.toLowerCase().replace(/\s+/g, '-')}`}>
          {isFr ? "Nouveau" : "New"}
        </span>
      )}
      <div className="relative flex flex-col justify-end h-full p-4 md:p-5 min-h-[150px] md:min-h-[190px]">
      <div
        className={`mx-auto w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/35 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform ${svc.accent}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <p
        className="text-white text-sm md:text-base font-semibold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {isFr ? svc.nameFr : svc.name}
      </p>
      {svc.tagline && (
        <p className="text-white/85 text-[10px] md:text-[11px] mt-1 leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
          {isFr ? (svc.taglineFr || svc.tagline) : svc.tagline}
        </p>
      )}
      <ArrowRight
        className={`w-4 h-4 mx-auto mt-2 ${svc.accent} opacity-0 group-hover:opacity-100 transition-opacity`}
      />
      </div>
    </Link>
  );
}

export default function TreatmentCategoriesGrid({ openBookingModal }) {
  const { language } = useLanguage();
  const isFr = language === "fr";

  return (
    <section
      className="py-10 sm:py-14 bg-stone-50"
      data-testid="treatment-categories-grid"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-7">
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-stone-500 mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {isFr ? "Explorer nos traitements" : "Explore Our Treatments"}
          </p>
          <h2
            className="text-2xl sm:text-3xl text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {isFr
              ? "Trouvez le bon traitement pour vous"
              : "Find the right treatment for you"}
          </h2>
          <p className="text-stone-500 text-sm mt-2 max-w-xl mx-auto">
            {isFr
              ? "Touchez une catégorie pour voir les prix et réserver."
              : "Tap any category to view pricing and book instantly."}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {TREATMENT_CATEGORY_CARDS.map((svc) => (
            <TreatmentCategoryCard key={svc.name} svc={svc} isFr={isFr} />
          ))}
        </div>

        {/* Quick Book CTA — directly under the grid so the most common
            action is one tap away even if no category jumps out. */}
        <div className="text-center mt-7">
          <button
            type="button"
            onClick={() => {
              if (openBookingModal) openBookingModal();
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-stone-900 hover:bg-stone-800 text-amber-400 text-sm font-bold uppercase tracking-wider transition-transform hover:scale-[1.02]"
            data-testid="tcg-book-now"
          >
            {isFr ? "Réserver maintenant" : "Book Now"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
